/**
 * CandidatePicker — Phase 4d shared widget.
 *
 * Used in two flows:
 *   1. Step 0 of the recording flow (RecordingFlowController) — pick a
 *      candidate BEFORE consent. Required, no escape hatch.
 *   2. Retroactive "Link to candidate" modal on InterviewDetail + on the
 *      Settings → Unlinked interviews list.
 *
 * Behavior:
 *   - Type-ahead search against /candidates/search (workspace-scoped,
 *     debounced 200ms, returns name + email + round_count).
 *   - "+ Create new candidate" row pinned at the top. Clicking expands a
 *     name-only inline form (email + linkedin optional) so the
 *     "record-now" moment stays one field + Continue.
 *   - On submit, calls onSelect({candidate_id, candidate_name, round_count}).
 *   - The parent owns the round_number computation + override UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, X, Loader2, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STROKE = 1.5;
const DEBOUNCE_MS = 200;

export default function CandidatePicker({ onSelect, autoFocus = true, nameOnly = false }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newLinkedin, setNewLinkedin] = useState("");
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Initial load + debounced re-search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/candidates/search`, {
          params: { q: query, limit: 20 },
        });
        setResults(data.candidates || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  async function createCandidate({ name, email, linkedin }) {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post(`/candidates`, {
        name: trimmed,
        email: email?.trim() || undefined,
        linkedin_url: linkedin?.trim() || undefined,
      });
      onSelect({
        candidate_id: data.candidate_id,
        candidate_name: data.name,
        round_count: 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create candidate");
    } finally {
      setCreating(false);
    }
  }

  async function handleCreate() {
    await createCandidate({ name: newName, email: newEmail, linkedin: newLinkedin });
  }

  // nameOnly mode (E1 recording flow): Enter either picks an exact existing
  // match or creates the candidate with the typed name — nothing else asked.
  function handleSearchKeyDown(e) {
    if (!nameOnly || e.key !== "Enter") return;
    e.preventDefault();
    const q = query.trim();
    if (!q || creating) return;
    const exact = results.find(
      (c) => (c.name || "").trim().toLowerCase() === q.toLowerCase()
    );
    if (exact) {
      onSelect({
        candidate_id: exact.candidate_id,
        candidate_name: exact.name,
        round_count: exact.round_count || 0,
      });
      return;
    }
    createCandidate({ name: q });
  }

  const inlineFormDisabled = !newName.trim() || creating;

  return (
    <div className="flex flex-col gap-3" data-testid="candidate-picker">
      {/* Search field */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" strokeWidth={STROKE} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder={nameOnly ? "Type a name — press Enter to add them" : "Search candidates by name or email…"}
          data-testid="candidate-picker-search"
          className="hp-input w-full pl-8"
        />
        {searching && (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-400" strokeWidth={STROKE} />
        )}
      </div>

      {/* Inline-create row (always visible at top) */}
      {nameOnly ? (
        query.trim() &&
        !results.some((c) => (c.name || "").trim().toLowerCase() === query.trim().toLowerCase()) && (
          <button
            type="button"
            onClick={() => createCandidate({ name: query })}
            disabled={creating}
            className="flex items-center gap-2 rounded-md border border-dashed border-emerald-800/40 bg-emerald-50/40 px-3 py-2 text-left text-[13px] font-medium text-emerald-900 hover:border-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
            data-testid="candidate-picker-create-name-only"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />
            ) : (
              <Plus className="h-4 w-4" strokeWidth={STROKE} />
            )}
            Create &ldquo;{query.trim()}&rdquo;
          </button>
        )
      ) : !showCreate ? (
        <button
          type="button"
          onClick={() => {
            setShowCreate(true);
            // Pre-fill name from query if it looks like a name (not an email)
            if (query.trim() && !query.includes("@")) setNewName(query.trim());
          }}
          className="flex items-center gap-2 rounded-md border border-dashed border-emerald-800/40 bg-emerald-50/40 px-3 py-2 text-left text-[13px] font-medium text-emerald-900 hover:border-emerald-800 hover:bg-emerald-50"
          data-testid="candidate-picker-create-toggle"
        >
          <Plus className="h-4 w-4" strokeWidth={STROKE} />
          Create new candidate
          {query.trim() && !query.includes("@") && (
            <span className="text-ink-500 font-normal">named "{query.trim()}"</span>
          )}
        </button>
      ) : (
        <div className="rounded-md border border-emerald-800/40 bg-white p-3 space-y-2" data-testid="candidate-picker-create-form">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-medium text-ink-700">New candidate</div>
            <button
              onClick={() => { setShowCreate(false); setNewName(""); setNewEmail(""); setNewLinkedin(""); }}
              className="text-ink-400 hover:text-ink-700"
              type="button"
              aria-label="Cancel new candidate"
            >
              <X className="h-3.5 w-3.5" strokeWidth={STROKE} />
            </button>
          </div>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name (required)"
            data-testid="candidate-picker-new-name"
            autoFocus
            className="hp-input w-full"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Email (optional)"
              data-testid="candidate-picker-new-email"
              className="hp-input w-full"
            />
            <input
              type="url"
              value={newLinkedin}
              onChange={(e) => setNewLinkedin(e.target.value)}
              placeholder="LinkedIn (optional)"
              data-testid="candidate-picker-new-linkedin"
              className="hp-input w-full"
            />
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={inlineFormDisabled}
            data-testid="candidate-picker-create-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
          >
            {creating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} />
            ) : (
              <Check className="h-3.5 w-3.5" strokeWidth={STROKE} />
            )}
            Create &amp; continue
          </button>
        </div>
      )}

      {/* Results list */}
      <div className="max-h-72 overflow-y-auto rounded-md border border-ink-200 bg-white" data-testid="candidate-picker-results">
        {results.length === 0 && !searching && (
          <div className="px-3 py-4 text-center text-[12px] text-ink-500">
            {query.trim()
              ? "No matches — create a new candidate above"
              : "No candidates yet — create one above to start"}
          </div>
        )}
        {results.map((c) => (
          <button
            key={c.candidate_id}
            type="button"
            onClick={() => onSelect({
              candidate_id: c.candidate_id,
              candidate_name: c.name,
              round_count: c.round_count || 0,
            })}
            className="flex w-full items-center justify-between gap-2 border-b border-ink-100 px-3 py-2 text-left text-[13px] hover:bg-emerald-50/50 last:border-b-0"
            data-testid={`candidate-picker-result-${c.candidate_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-ink-900">{c.name}</div>
              {c.email && (
                <div className="truncate text-[11px] text-ink-500">{c.email}</div>
              )}
            </div>
            <div className="shrink-0 text-[11px] text-ink-500">
              {c.round_count} round{c.round_count === 1 ? "" : "s"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
